import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import Text from "./Text";
import Img from "./Img";
import { buildWhatsappUrl } from "../../../Components/Utils";

const QuickViewModal = ({ product, onClose }) => {
  const [imgIndex, setImgIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  const images = [product.img, product.hoverImg].filter(Boolean);
  console.log(images, product);
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    // document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => {
      // document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const prev = () =>
    setImgIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setImgIndex((i) => (i + 1) % images.length);

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-300 ${visible ? "bg-black/50 backdrop-blur-sm" : "bg-black/0"}`}
      onClick={handleClose}
    >
      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto flex flex-col md:flex-row transition-all duration-300 ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-95"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white shadow flex items-center justify-center hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Image side */}
        <div className="relative w-full md:w-[45%] flex-shrink-0 bg-gray-50 rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none overflow-hidden min-h-[300px]">
          <Img
            key={imgIndex}
            src={images[imgIndex]}
            alt={product.title}
            className="w-full h-full object-cover absolute inset-0 transition-opacity duration-300"
            style={{ aspectRatio: "275/386" }}
          />

          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors"
                aria-label="Next image"
              >
                <ChevronRight size={16} />
              </button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIndex(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imgIndex ? "bg-gray-900" : "bg-gray-400"}`}
                  />
                ))}
              </div>
            </>
          )}

          {product.badge && (
            <div className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow">
              {product.badge}
            </div>
          )}
        </div>

        {/* Info side */}
        <div className="flex-1 p-6 flex flex-col gap-4">
          {/* Badges */}
          {/* <div className="flex items-center gap-2 flex-wrap">
            <span className="border border-gray-300 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded">
              BEST SELLER
            </span>
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Zap size={12} className="text-blue-500" />
              Selling fast! 48 people have this in their carts.
            </span>
          </div> */}

          {/* Title */}
          <Text
            is="h2"
            variant="section-heading"
            className="text-xl text-gray-900 leading-tight"
          >
            {product.title}
          </Text>

          {/* Description */}
          {(product.short_description ?? product.description) && (
            <Text
              is="p"
              variant="body"
              className="flex-1 overflow-y-auto text-gray-500 text-sm leading-relaxed"
            >
              {(product?.short_description ?? product.description) && (
                <div
                  className="tiptap-content"
                  dangerouslySetInnerHTML={{
                    __html: (product.short_description ?? product.description) 
                  }}
                />
              )}
            </Text>
          )}

          {/* WhatsApp CTA */}
          <Text
            is="a"
            href={buildWhatsappUrl(product)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto w-full bg-[#25D366] hover:bg-[#1ebe5d] transition-colors text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 shadow-lg"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Order via WhatsApp
          </Text>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default QuickViewModal;
